import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import {
  Grid,
  Radio,
  Tooltip,
  MenuItem,
  RadioGroup,
  IconButton,
  FormControl,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';
import { RemoveRedEye } from '@mui/icons-material';

import { DataProfile, AlarmDefinition } from 'types/types';
import { TargetCriteriaValidationError } from '../CreateForm';

import Select, { SelectRef } from '../../../../Common/InputComponents/Select';

interface Props {
  dataProfiles: DataProfile[];
  handleShowPreviewClick: () => void;
  handleOnChange: (field?: string) => void;
  alarmDefinitionData?: AlarmDefinition | null;
  handleShowQueryBuilder: (show: boolean) => void;
  validationErrors: TargetCriteriaValidationError;
}

export interface Ref {
  selectedProfileOption: ProfileOption;
  dataProfileRef: React.RefObject<SelectRef>;
  handleProfileClick: (profileOption: ProfileOption) => () => void;
}

export enum ProfileOption {
  CUSTOM = 'Custom',
  PROFILE = 'Profile',
}

const DataProfiles: ForwardRefRenderFunction<Ref, Props> = (
  {
    dataProfiles,
    handleOnChange,
    validationErrors,
    alarmDefinitionData,
    handleShowQueryBuilder,
    handleShowPreviewClick,
  },
  ref
) => {
  const [selectedProfileOption, setSelectedProfileOption] =
    useState<ProfileOption>(
      alarmDefinitionData?.data_profile_sid
        ? ProfileOption.PROFILE
        : ProfileOption.CUSTOM
    );

  const dataProfileRef = useRef<SelectRef>(null);

  const handleProfileClick = (profileOption: ProfileOption) => () => {
    setSelectedProfileOption(profileOption);
    handleShowQueryBuilder(profileOption === ProfileOption.CUSTOM);
  };

  useImperativeHandle(ref, () => ({
    dataProfileRef,
    handleProfileClick,
    selectedProfileOption,
  }));

  useEffect(() => {
    if (alarmDefinitionData && alarmDefinitionData.data_profile_sid) {
      setSelectedProfileOption(ProfileOption.PROFILE);
      handleShowQueryBuilder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmDefinitionData]);

  return (
    <div className="target-criteria-data-profile">
      <RadioGroup name="severity" row className="radio-button-container">
        {Object.values(ProfileOption).map((target) => (
          <FormControlLabel
            key={`Target-${target}`}
            value={target}
            control={
              <Radio
                className="radio-button"
                checked={selectedProfileOption === target}
              />
            }
            label={target}
            onClick={handleProfileClick(target)}
          />
        ))}
      </RadioGroup>
      <Grid container sx={{ alignItems: 'center' }} gap={1}>
        {selectedProfileOption === ProfileOption.PROFILE && (
          <React.Fragment>
            <Grid item xs={5}>
              <FormControl fullWidth error={!!validationErrors.dataProfile}>
                <Select
                  fullWidth
                  size="small"
                  id="dataProfile"
                  className="input-elements"
                  value={alarmDefinitionData?.data_profile_sid ?? ''}
                  inputRef={dataProfileRef}
                  MenuProps={{
                    PaperProps: { sx: { maxHeight: 200 } },
                  }}
                  handleOnChange={() => handleOnChange('dataProfile')}
                >
                  {dataProfiles?.map((s) => (
                    <MenuItem
                      key={`Priority-${s.profileSid}`}
                      value={s.profileSid}
                    >
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{validationErrors.dataProfile}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={1}>
              <Tooltip title="Show Preview">
                <IconButton onClick={() => handleShowPreviewClick()}>
                  <RemoveRedEye />
                </IconButton>
              </Tooltip>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
    </div>
  );
};

export default forwardRef<Ref, Props>(DataProfiles);
